import React, { useRef, useState, useEffect } from 'react';
import { useDrag } from '@use-gesture/react';
import { useSprings, animated, to } from 'react-spring';
import swap from 'lodash-move';
import CollapsibleListContent from './CollapsibleListContent';

const rowHeight = 46;

const styles = {};

const clamp = (num, clamp, higher) => (higher ? Math.min(Math.max(num, clamp), higher) : Math.min(num, clamp));

const fn = (order, down, originalIndex, currIndex, y) => ind => (down && ind === originalIndex
  ? {
    y: currIndex * rowHeight + y,
    scale: 1,
    zIndex: '1',
    shadow: 15,
    immediate: n => n === 'y' || n === 'zIndex',
  }
  : {
    y: order.indexOf(ind) * rowHeight,
    scale: 1,
    zIndex: '0',
    shadow: 1,
    immediate: false,
  });

const initialState = {
  type: 'CollapsibleList',
  color: 'white',
  bgWhite: false,
  firstClosed: true,
  components: [],
};

const componentInitialState = {
  text: {
    en: '',
    es: '',
    ca: '',
    pt: '',
  },
  button: {
    category: '',
    text: {
      en: '',
      es: '',
      ca: '',
      pt: '',
    },
  },
  components: [],
};

const CollapsibleListComponent = ({
  activeLanguage,
  reusableComponents,
  handlerSetComponents,
  components,
  id,
  reusableComponent,
  reusableComponentSlugname,
  languages,
}) => {
  const [state, setState] = useState(initialState);
  const [selected, setSelected] = useState([]);
  const [order, setOrder] = useState([], [selected]);
  const [index, setIndex] = useState();
  // Create springs, each corresponds to an item, controlling its transform, scale, etc.
  const [springs, springApi] = useSprings(selected.length, fn(order), [
    selected,
    order,
  ]);
  const [isBgWhite, setBgWhite] = useState(false);
  const [isFirstClosed, setFirstClosed] = useState(true);
  // Preps a gesture handler which returns drag-deltas, touched/clicked state, etc.
  useEffect(() => {
    setSelected(state.components);
  }, [state]);

  const bind = useDrag(({ args: [originalIndex], down, movement: [, y] }) => {
    // Bunch of math to calculate current row and new order,
    const curIndex = order.indexOf(originalIndex);
    const curRow = clamp(
      Math.round((curIndex * rowHeight + y) / rowHeight),
      0,
      selected.length - 1,
    );
    const newOrder = swap(order, curIndex, curRow);

    springApi.start(fn(newOrder, down, originalIndex, curIndex, y));

    if (!down) {
      setOrder(newOrder);

      // reorder selected and update component on PostContainer
      const newComponentsOrder = newOrder.map(index => selected[index]);

      const newState = {
        ...state,
        components: newComponentsOrder,
      };
      setState(newState);
      const componentObj = {
        id,
        component: newState,
      };
      handlerSetComponents(componentObj, index);
    }
  });

  const updateComponentInParentState = (comp, compIndex) => {
    const newSelected = selected;

    newSelected[compIndex] = comp;

    const newComponentsOrder = order.map(index => newSelected[index]);

    const newState = {
      ...state,
      components: newComponentsOrder,
    };
    setState(newState);
    const componentObj = {
      id,
      component: newState,
    };
    handlerSetComponents(componentObj, index);
  };

  const deleteItemHandler = (itemIndex) => {
    const newArr = selected;
    newArr.splice(itemIndex, 1);
    const newState = {
      ...state,
      components: newArr,
    };
    setState(newState);
    const ii = order.findIndex(num => num === itemIndex);
    const n = [
      ...order.slice(0, ii),
      ...order.slice(ii + 1).map(num => (num > 0 && num > ii ? num - 1 : num)),
    ];
    setOrder(n);
  };

  const addItemHandler = () => {
    setSelected([...selected, componentInitialState]);
    setOrder([...order, order.length]);
  };

  useEffect(() => {
    if (index >= 0 && components.length) {
      const { component } = components[index];
      const comp = {
        ...component,
        bgWhite: isBgWhite,
        firstClosed: isFirstClosed,
      };
      setState(comp);

      const componentObj = {
        id,
        component: comp,
      };

      handlerSetComponents(componentObj, index);
    }
  }, [isBgWhite, isFirstClosed]);

  useEffect(() => {
    const componentIndex = components.findIndex(obj => obj.id === id);
    setIndex(componentIndex);
    const { component } = components[componentIndex];
    if (reusableComponent) {
      setState(reusableComponent);
      if (!component) {
        handlerSetComponents(
          { component: { key: reusableComponentSlugname } },
          componentIndex,
        );
      }
    } else {
      // setIndex(componentIndex);

      if (component) {
        setSelected(component.components);
        let comp = { ...component };
        comp = {
          ...component,
          bgWhite: component.bgWhite,
          firstClosed: component.firstClosed,
        };
        setBgWhite(component.bgWhite);
        setFirstClosed(component.firstClosed);
        setState(comp);
        setOrder(component.components.map((_, i) => i));
        // order.current = selected.map((_, index) => index);
      } else {
        const componentObj = {
          id,
          component: state,
        };
        handlerSetComponents(componentObj, componentIndex);
      }
    }
  }, []);

  return (
    <div
      className="content"
      style={{ height: selected.length * rowHeight + 50 }}
    >
      <div className="flex items-center h-12 pl-1/2">
        <button
          type="button"
          onClick={addItemHandler}
          className="font-americaMonoBold text-xs pl-10 pr-6 py-1 h-7 ml-12 my-1 rounded-full uppercase bg-greyFilter flex flex-row items-center"
        >
          add new
          <span className="font-icon icon-add-button text-base pl-2" />
        </button>
        <div className="font-americaMonoRegular text-xs uppercase text-black ml-4">
          <label htmlFor="bgwhite">
            White background
            <input
              className="ml-2"
              id="bgwhite"
              name="bgwhite"
              checked={isBgWhite}
              onChange={() => setBgWhite(!isBgWhite)}
              type="checkbox"
            />
          </label>
          <label htmlFor="firstClosed" className="ml-2">
            First closed
            <input
              className="ml-2"
              id="firstClosed"
              name="firstClosed"
              checked={isFirstClosed}
              onChange={() => setFirstClosed(!isFirstClosed)}
              type="checkbox"
            />
          </label>
        </div>
      </div>
      {springs.map(({
        zIndex, shadow, y, scale,
      }, i) => (
        <animated.div
          {...bind(i)}
          key={i}
          style={{
            zIndex,
            // @use-gesture 10 needs this or the browser scrolls instead of dragging.
            touchAction: 'none',
            boxShadow: shadow.to(
              s => `rgba(0, 0, 0, 0.15) 0px ${s}px ${2 * s}px 0px`,
            ),
            transform: to([y], (y, s) => `translate3d(0,${y}px,0)`),
          }}
        >
          <CollapsibleListContent
            reusableComponents={reusableComponents}
            languages={languages}
            activeLanguage={activeLanguage}
            selected={selected}
            component={selected[i]}
            index={i}
            deleteItemHandler={deleteItemHandler}
            updateComponentInParentState={updateComponentInParentState}
          />
        </animated.div>
      ))}
    </div>
  );
};
export default CollapsibleListComponent;
